import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePermissions } from 'react-admin';
import { httpClient } from '../utils/dataProvider';
import { API_BASE_URL } from '../utils/config';
import { hasAccess } from '../utils/permissions';
import { validateValue, extractPlaceholders, codePointLength } from '../utils/translationValidation';
import {
    PageShell, PageHeader, Surface, Row, RowText, RowMeta, StatusPill, ActionButton,
    StatePanel, MetricCard, Empty,
} from '../components/ui';
import { tokens } from '../theme';

// Remote-controlled EN/AR copy for the app and web. Edits are saved as drafts;
// a SUPER_ADMIN publishes them and clients pick them up on their next launch.
// Contract: skilzBackend/app/routes/translations.js (mounted at /api/translations).

const API = `${API_BASE_URL}/translations`;
const LIST_PAGE_SIZE = 200; // backend max per request
const ROWS_PER_PAGE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const MAX_IMPORT_BYTES = 5 * 1024 * 1024;
const PREVIEW_LIMIT = 200;
const PURPLE = '#7c3aed';
const COUNTER_AMBER = '#9a3d13';
const COUNTER_RED = '#b3145a';
const LOCALES = ['en', 'ar'];
const LOCALE_LABEL = { en: 'English', ar: 'Arabic' };

const TABS = [
    { id: 'all', label: 'Published', summaryKey: 'totalKeys', tone: 'tone-green' },
    { id: 'draft', label: 'Drafts', summaryKey: 'draftsPending', tone: 'tone-orange' },
    { id: 'overridden', label: 'Live overrides', summaryKey: 'overridesLive', tone: 'tone-green' },
    { id: 'overLimit', label: 'Over limit', summaryKey: 'overLimitDrafts', tone: 'tone-orange' },
    { id: 'stale', label: 'Stale', summaryKey: 'staleOverrides', tone: 'tone-orange' },
];

const EXPORT_SCOPES = [
    { id: 'all', label: 'All strings' },
    { id: 'drafts', label: 'Drafts only' },
    { id: 'overridden', label: 'Live overrides only' },
];

const ISSUE_TEXT = {
    EMPTY: 'Cannot be empty',
    OVER_LIMIT: 'Over limit — cannot publish',
    PLACEHOLDER_MISMATCH: 'Placeholders must match the bundled text',
    CONTROL_CHARS: 'Contains control characters',
    NEWLINE_NOT_ALLOWED: 'Line breaks are not allowed here',
    NEAR_LIMIT: 'Close to the limit',
    AR_NO_ARABIC: 'No Arabic characters',
    EN_HAS_ARABIC: 'Contains Arabic characters',
    WHITESPACE_TRIMMED: 'Leading/trailing spaces will be trimmed',
    DRAFT_CHANGED: 'Edited while publishing — still a draft',
    RESTORED_INVALID: 'Old value no longer passes validation',
    CHANGED_SINCE: 'Changed since that release',
    MISSING: 'String no longer exists',
    UNKNOWN_REF: 'Unknown ref',
    UNKNOWN_KEY: 'Unknown key',
    RETIRED_REF: 'Retired string',
    DUPLICATE_ROW: 'Duplicate row',
    REF_KEY_MISMATCH: 'Ref and key point at different strings',
    MISSING_LOCALE: 'No string for this locale',
};

// ── Styles (DESIGN.md: radius ≥ 8 on inputs, fields stacked vertically) ─────

const styles = {
    input: {
        width: '100%',
        padding: '8px 12px',
        borderRadius: 8,
        border: '1px solid #e7eaf0',
        fontSize: 14,
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    },
    textarea: {
        width: '100%',
        minHeight: 64,
        padding: '10px 12px',
        borderRadius: 8,
        border: '1px solid #e7eaf0',
        fontSize: 14,
        lineHeight: 1.5,
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        resize: 'vertical',
    },
    search: {
        width: '100%',
        maxWidth: 420,
        padding: '9px 12px',
        borderRadius: 8,
        border: `1.5px solid ${PURPLE}`,
        fontSize: 13,
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    },
    select: {
        padding: '8px 10px',
        borderRadius: 8,
        border: '1px solid #e7eaf0',
        fontSize: 13,
        fontFamily: 'inherit',
        background: '#fff',
    },
    label: { fontSize: 12, fontWeight: 600, color: '#566071', marginBottom: 4, display: 'block' },
    stack: { display: 'flex', flexDirection: 'column', gap: 12 },
    mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
    muted: { fontSize: 12, color: '#7f8999' },
    bundled: {
        fontSize: 12,
        color: '#7f8999',
        background: '#f7f8fa',
        borderRadius: 8,
        padding: '6px 10px',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
    },
    linkButton: {
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: PURPLE,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: 'inherit',
    },
    secondaryButton: { background: '#fff', borderColor: '#e4e8f0', color: '#14161f' },
    success: { padding: '8px 14px', borderRadius: 8, background: '#dff9eb', color: '#096844', fontSize: 13 },
    error: { padding: '8px 14px', borderRadius: 8, background: '#ffeef5', color: '#b3145a', fontSize: 13 },
    warn: { padding: '8px 14px', borderRadius: 8, background: '#fff0e7', color: '#9a3d13', fontSize: 13 },
    alertCard: { background: '#ef4444', color: '#fff', borderRadius: 12, padding: 16, fontSize: 13 },
    list: { margin: '6px 0 0', paddingLeft: 18, fontSize: 12, lineHeight: 1.6 },
    pillButton: active => ({
        padding: '5px 14px',
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'inherit',
        border: active ? `2px solid ${PURPLE}` : '1px solid var(--sk-border)',
        background: active ? PURPLE : 'var(--sk-surface)',
        color: active ? '#fff' : '#566071',
    }),
    overlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        background: 'rgba(20, 22, 31, 0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '6vh 16px',
        overflow: 'auto',
    },
};

// ── Pure helpers ─────────────────────────────────────────────────────────────

const bundledOf = view => {
    const b = (view && view.bundled) || {};
    if (b.app !== null && b.app !== undefined) return b.app;
    if (b.web !== null && b.web !== undefined) return b.web;
    return null;
};

// Same rule as the backend: newlines are allowed if either bundle has one.
const bundledForNewlineRule = view => {
    const b = (view && view.bundled) || {};
    if (typeof b.app === 'string' && b.app.includes('\n')) return b.app;
    if (typeof b.web === 'string' && b.web.includes('\n')) return b.web;
    return bundledOf(view);
};

/** The value the editor starts from: pending draft → live override → bundled. */
const serverValue = view => {
    if (!view) return '';
    if (view.status === 'draft' && view.draftClear) return bundledOf(view) || '';
    if (view.status === 'draft' && view.draftValue !== null && view.draftValue !== undefined) return view.draftValue;
    if (view.publishedValue !== null && view.publishedValue !== undefined) return view.publishedValue;
    return bundledOf(view) || '';
};

const validateFor = (view, value) => validateValue({
    value,
    locale: view.locale,
    maxLength: view.maxLength,
    placeholders: view.placeholders || [],
    bundled: bundledForNewlineRule(view),
});

const blockingErrors = errors => (errors || []).filter(e => e.code !== 'OVER_LIMIT');

const issueText = issue => ISSUE_TEXT[issue && issue.code] || (issue && (issue.message || issue.code)) || 'Error';

/** `{ code, details }` from a react-admin HttpError (body is the parsed JSON). */
const readError = err => {
    const body = (err && err.body) || {};
    if (Array.isArray(body.errors)) return { code: 'VALIDATION_FAILED', details: body };
    const code = (body.errors && body.errors.msg) || (err && err.message) || 'ERROR_TRY_AGAIN';
    return { code, details: body };
};

const formatDate = value => {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const filenameFrom = (disposition, fallback) => {
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition || '');
    if (!match) return fallback;
    try {
        return decodeURIComponent(match[1]);
    } catch (e) {
        return match[1];
    }
};

const copyText = text => {
    if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(text);
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    return Promise.resolve();
};

/** Every page of the list for the current server-side filters (≈1.4k keys). */
const fetchAllStrings = async params => {
    const build = page => {
        const qs = new URLSearchParams({ ...params, page: String(page), limit: String(LIST_PAGE_SIZE) });
        return `${API}?${qs.toString()}`;
    };
    const { json } = await httpClient(build(1));
    const first = (json && json.docs) || [];
    const total = (json && json.total) || first.length;
    const pages = Math.ceil(total / LIST_PAGE_SIZE);
    if (pages <= 1) return first;
    const rest = await Promise.all(
        Array.from({ length: pages - 1 }, (_, i) => httpClient(build(i + 2)).then(r => (r.json && r.json.docs) || []))
    );
    return first.concat(...rest);
};

// ── Small presentational pieces ──────────────────────────────────────────────

const Tag = ({ color, children, title }) => (
    <span className="sk-pill" title={title} style={{ color, backgroundColor: `${color}1f` }}>{children}</span>
);

const Modal = ({ title, description, onClose, busy, children }) => (
    <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={styles.overlay}
        onClick={e => { if (e.target === e.currentTarget && !busy) onClose(); }}
        onKeyDown={e => { if (e.key === 'Escape' && !busy) onClose(); }}
    >
        <div style={{ width: 'min(760px, 100%)' }}>
            <Surface title={title} description={description}>
                {children}
            </Surface>
        </div>
    </div>
);

const IssueList = ({ items, render }) => {
    const list = items || [];
    if (!list.length) return null;
    return (
        <ul style={styles.list}>
            {list.map((item, i) => <li key={i}>{render(item)}</li>)}
        </ul>
    );
};

const describeItem = item => `${item.ref || '—'} · ${item.key || '—'}${item.locale ? ` · ${item.locale.toUpperCase()}` : ''}`;

// ── Locale editor (one per EN / AR) ──────────────────────────────────────────

const Counter = ({ length, max }) => {
    const ratio = max > 0 ? length / max : 0;
    const color = ratio > 1 ? COUNTER_RED : ratio > 0.9 ? COUNTER_AMBER : '#7f8999';
    return (
        <span style={{ fontSize: 12, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
            {length} / {max || '—'}
            {ratio > 1 && <span style={{ marginLeft: 6 }}>Over limit — cannot publish</span>}
        </span>
    );
};

const PlaceholderChips = ({ expected, value }) => {
    const actual = extractPlaceholders(value);
    const extra = actual.filter(p => !expected.includes(p));
    if (!expected.length && !extra.length) return null;
    return (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {expected.map(p => (
                <Tag key={`e-${p}`} color={actual.includes(p) ? tokens.success : tokens.danger} title={actual.includes(p) ? 'Present' : 'Missing'}>
                    <span style={styles.mono}>{`{${p}}`}</span>{!actual.includes(p) && ' missing'}
                </Tag>
            ))}
            {extra.map(p => (
                <Tag key={`x-${p}`} color={tokens.danger} title="Not in the bundled text">
                    <span style={styles.mono}>{`{${p}}`}</span> unexpected
                </Tag>
            ))}
        </div>
    );
};

const LocaleEditor = ({ group, locale, edit, result, onChange, onClear, onUndo }) => {
    const view = group[locale];
    if (!view) {
        return (
            <div style={styles.stack}>
                <span style={styles.label}>{LOCALE_LABEL[locale]}</span>
                <span style={styles.muted}>No {LOCALE_LABEL[locale]} string exists for this key.</span>
            </div>
        );
    }
    const localeView = { ...view, locale };
    const clearing = !!(edit && edit.clear && edit.clear[locale]);
    const value = clearing ? (bundledOf(view) || '') : (edit && edit[locale] !== undefined ? edit[locale] : serverValue(view));
    const validation = validateFor(localeView, value);
    const length = codePointLength(value.trim());
    const blocking = blockingErrors(validation.errors);
    const overLimit = validation.errors.some(e => e.code === 'OVER_LIMIT');
    const near = validation.warnings.some(w => w.code === 'NEAR_LIMIT');
    const borderColor = blocking.length || overLimit ? COUNTER_RED : near ? COUNTER_AMBER : '#e7eaf0';
    const app = view.bundled && view.bundled.app;
    const web = view.bundled && view.bundled.web;
    const bundledDiffers = typeof app === 'string' && typeof web === 'string' && app !== web;
    const id = `tr-${group.ref}-${locale}`;

    return (
        <div style={styles.stack}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <label htmlFor={id} style={{ ...styles.label, marginBottom: 0 }}>
                    {LOCALE_LABEL[locale]} <span style={styles.muted}>(max {view.maxLength || '—'})</span>
                </label>
                <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {view.status === 'draft' && <StatusPill status="draft" />}
                    {view.publishedValue !== null && view.publishedValue !== undefined && <Tag color={tokens.success}>live override</Tag>}
                    {view.stale && <Tag color={tokens.info} title="The bundled text changed after this override was published">stale</Tag>}
                </span>
            </div>
            <textarea
                id={id}
                dir={locale === 'ar' ? 'rtl' : 'ltr'}
                lang={locale}
                value={value}
                disabled={clearing}
                onChange={e => onChange(locale, e.target.value)}
                aria-invalid={blocking.length > 0 || overLimit}
                style={{ ...styles.textarea, borderColor, opacity: clearing ? 0.6 : 1 }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Counter length={length} max={view.maxLength} />
                {clearing ? (
                    <span style={styles.muted}>
                        Will revert to bundled on save · <button type="button" style={styles.linkButton} onClick={() => onUndo(locale)}>Undo</button>
                    </span>
                ) : (
                    <button type="button" style={styles.linkButton} onClick={() => onClear(locale)}>Revert to bundled</button>
                )}
            </div>
            <PlaceholderChips expected={view.placeholders || []} value={value} />
            {!clearing && (validation.errors.length > 0 || validation.warnings.length > 0) && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {validation.errors.filter(e => e.code !== 'OVER_LIMIT').map(e => <Tag key={e.code} color={tokens.danger}>{issueText(e)}</Tag>)}
                    {validation.warnings.map(w => <Tag key={w.code} color={tokens.warning}>{issueText(w)}</Tag>)}
                </div>
            )}
            {(view.draftErrors || []).length > 0 && !(edit && (edit[locale] !== undefined || clearing)) && (
                <span style={{ fontSize: 12, color: COUNTER_RED }}>Saved draft fails validation: {(view.draftErrors || []).map(issueText).join(', ')}</span>
            )}
            <div>
                <span style={styles.muted}>{bundledDiffers ? 'Bundled (app)' : 'Bundled'}</span>
                <div style={styles.bundled} dir={locale === 'ar' ? 'rtl' : 'ltr'}>{bundledOf(view) || '—'}</div>
            </div>
            {bundledDiffers && (
                <div>
                    <span style={styles.muted}>Bundled (web)</span>
                    <div style={styles.bundled} dir={locale === 'ar' ? 'rtl' : 'ltr'}>{web}</div>
                </div>
            )}
            {view.publishedValue !== null && view.publishedValue !== undefined && view.status === 'draft' && (
                <div>
                    <span style={styles.muted}>Currently live</span>
                    <div style={styles.bundled} dir={locale === 'ar' ? 'rtl' : 'ltr'}>{view.publishedValue}</div>
                </div>
            )}
            {result && (
                <span style={{ fontSize: 12, fontWeight: 600, color: result.state === 'saved' ? tokens.success : result.state === 'flagged' ? COUNTER_AMBER : COUNTER_RED }}>
                    {result.message}
                </span>
            )}
        </div>
    );
};

// ── SUPER_ADMIN: per-key limits / tier ───────────────────────────────────────

const MetaEditor = ({ group, onSaved }) => {
    const [tier, setTier] = useState(group.tier || 'normal');
    const [enMax, setEnMax] = useState(group.en ? String(group.en.maxLength || '') : '');
    const [arMax, setArMax] = useState(group.ar ? String(group.ar.maxLength || '') : '');
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);

    const save = () => {
        const calls = [];
        if (tier !== group.tier) calls.push({ ref: group.ref, tier });
        if (group.en && enMax !== String(group.en.maxLength || '')) calls.push({ ref: group.ref, locale: 'en', maxLength: Number(enMax) });
        if (group.ar && arMax !== String(group.ar.maxLength || '')) calls.push({ ref: group.ref, locale: 'ar', maxLength: Number(arMax) });
        if (!calls.length) {
            setMessage({ ok: true, text: 'Nothing changed.' });
            return;
        }
        setBusy(true);
        setMessage(null);
        calls.reduce(
            (p, body) => p.then(() => httpClient(`${API}/meta`, { method: 'PATCH', body: JSON.stringify(body) })),
            Promise.resolve()
        )
            .then(() => {
                setMessage({ ok: true, text: 'Limits saved.' });
                onSaved();
            })
            .catch(err => {
                console.error('Translations meta update failed:', err);
                setMessage({ ok: false, text: `Could not save limits (${readError(err).code}).` });
            })
            .finally(() => setBusy(false));
    };

    return (
        <div style={{ ...styles.stack, borderTop: '1px solid var(--sk-border-soft)', paddingTop: 12 }}>
            <span style={styles.label}>Limits (Super Admin)</span>
            <label style={styles.label}>
                Tier
                <select style={{ ...styles.select, display: 'block', marginTop: 4 }} value={tier} onChange={e => setTier(e.target.value)}>
                    <option value="tight">tight (button / tab / badge)</option>
                    <option value="normal">normal</option>
                    <option value="long">long</option>
                </select>
            </label>
            {group.en && (
                <label style={styles.label}>
                    EN max length
                    <input style={{ ...styles.input, marginTop: 4, maxWidth: 160 }} type="number" min="1" max="2000" value={enMax} onChange={e => setEnMax(e.target.value)} />
                </label>
            )}
            {group.ar && (
                <label style={styles.label}>
                    AR max length
                    <input style={{ ...styles.input, marginTop: 4, maxWidth: 160 }} type="number" min="1" max="2000" value={arMax} onChange={e => setArMax(e.target.value)} />
                </label>
            )}
            <div>
                <ActionButton type="button" variant="secondary" style={styles.secondaryButton} disabled={busy} onClick={save}>
                    {busy ? 'Saving…' : 'Save limits'}
                </ActionButton>
            </div>
            {message && <div style={message.ok ? styles.success : styles.error}>{message.text}</div>}
        </div>
    );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const Translations = () => {
    const { permissions, loaded: permissionsLoaded } = usePermissions();
    const adminRoles = (permissions && permissions.adminRoles) || [];
    const isSuperAdmin = adminRoles.includes('SUPER_ADMIN');
    const canAccess = hasAccess('translations', adminRoles);

    // Summary + list
    const [summary, setSummary] = useState(null);
    const [summaryError, setSummaryError] = useState(false);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);
    const requestId = useRef(0);

    // Filters
    const [tab, setTab] = useState('all');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [platform, setPlatform] = useState('all');
    const [tier, setTier] = useState('all');
    const [area, setArea] = useState('all');
    const [page, setPage] = useState(1);

    // Editing
    const [expanded, setExpanded] = useState({});
    const [edits, setEdits] = useState({});
    const [results, setResults] = useState({});
    const [saving, setSaving] = useState(false);
    const [notice, setNotice] = useState(null);
    const [copiedRef, setCopiedRef] = useState(null);

    // CSV
    const [exportScope, setExportScope] = useState('all');
    const [exporting, setExporting] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [importState, setImportState] = useState({ fileName: '', content: '', preview: null, result: null, loading: false, error: null });
    const fileInput = useRef(null);

    // Publish / releases / kill switch
    const [publishOpen, setPublishOpen] = useState(false);
    const [publishNotes, setPublishNotes] = useState('');
    const [publishBusy, setPublishBusy] = useState(false);
    const [publishOutcome, setPublishOutcome] = useState(null);
    const [releases, setReleases] = useState([]);
    const [releasesLoading, setReleasesLoading] = useState(true);
    const [releasesError, setReleasesError] = useState(false);
    const [releaseDetail, setReleaseDetail] = useState({});
    const [rollbackTarget, setRollbackTarget] = useState(null);
    const [rollbackNotes, setRollbackNotes] = useState('');
    const [rollbackBusy, setRollbackBusy] = useState(false);
    const [rollbackOutcome, setRollbackOutcome] = useState(null);
    const [killOpen, setKillOpen] = useState(false);
    const [killNotes, setKillNotes] = useState('');
    const [killBusy, setKillBusy] = useState(false);
    const [killError, setKillError] = useState(null);

    // ── Loading ──────────────────────────────────────────────────────────────

    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [searchInput]);

    const loadSummary = useCallback(() => {
        httpClient(`${API}/summary`)
            .then(({ json }) => {
                setSummary(json || null);
                setSummaryError(false);
            })
            .catch(err => {
                console.error('Translations summary failed:', err);
                setSummaryError(true);
            });
    }, []);

    const loadReleases = useCallback(() => {
        setReleasesLoading(true);
        httpClient(`${API}/releases?page=1&limit=20`)
            .then(({ json }) => {
                setReleases((json && json.docs) || []);
                setReleasesError(false);
            })
            .catch(err => {
                console.error('Translations releases failed:', err);
                setReleasesError(true);
            })
            .finally(() => setReleasesLoading(false));
    }, []);

    const refreshAll = useCallback(() => {
        loadSummary();
        loadReleases();
        setReloadToken(t => t + 1);
    }, [loadSummary, loadReleases]);

    useEffect(() => {
        if (!canAccess) return;
        loadSummary();
        loadReleases();
    }, [canAccess, loadSummary, loadReleases]);

    useEffect(() => {
        if (!canAccess) return;
        const id = ++requestId.current;
        const params = {};
        if (tab !== 'all') params.status = tab;
        if (search) params.q = search;
        if (platform !== 'all') params.platform = platform;
        if (tier !== 'all') params.tier = tier;
        setLoading(true);
        setLoadError(null);
        fetchAllStrings(params)
            .then(docs => {
                if (id !== requestId.current) return;
                setGroups(Array.isArray(docs) ? docs : []);
            })
            .catch(err => {
                console.error('Translations list failed:', err);
                if (id === requestId.current) setLoadError(readError(err).code);
            })
            .finally(() => {
                if (id === requestId.current) setLoading(false);
            });
    }, [canAccess, tab, search, platform, tier, reloadToken]);

    useEffect(() => { setPage(1); }, [tab, search, platform, tier, area]);

    // ── Derived ──────────────────────────────────────────────────────────────

    const areaCounts = useMemo(() => {
        const counts = {};
        groups.forEach(g => {
            const a = g.area || 'general';
            counts[a] = (counts[a] || 0) + 1;
        });
        return counts;
    }, [groups]);

    useEffect(() => {
        if (area !== 'all' && !areaCounts[area]) setArea('all');
    }, [area, areaCounts]);

    const visible = useMemo(
        () => (area === 'all' ? groups : groups.filter(g => (g.area || 'general') === area)),
        [groups, area]
    );
    const pageCount = Math.max(1, Math.ceil(visible.length / ROWS_PER_PAGE));
    const pageRows = visible.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

    const groupByKey = useMemo(() => {
        const map = {};
        groups.forEach(g => { map[g.key] = g; });
        return map;
    }, [groups]);

    /** Dirty locales for one key → { en?, ar?, clear: [] } or null. */
    const pendingFor = useCallback((group, edit) => {
        if (!group || !edit) return null;
        const item = {};
        const clear = [];
        LOCALES.forEach(locale => {
            const view = group[locale];
            if (!view) return;
            if (edit.clear && edit.clear[locale]) {
                clear.push(locale);
            } else if (edit[locale] !== undefined && edit[locale] !== serverValue(view)) {
                item[locale] = edit[locale];
            }
        });
        if (!clear.length && item.en === undefined && item.ar === undefined) return null;
        return { ...item, clear };
    }, []);

    const pendingBlocked = useCallback((group, pending) => LOCALES.some(locale => {
        if (!pending || pending[locale] === undefined) return false;
        return blockingErrors(validateFor({ ...group[locale], locale }, pending[locale]).errors).length > 0;
    }), []);

    const pendingList = useMemo(() => Object.keys(edits)
        .map(key => {
            const group = groupByKey[key] || (edits[key] && edits[key].group);
            const pending = pendingFor(group, edits[key]);
            return pending ? { key, group, pending, blocked: pendingBlocked(group, pending) } : null;
        })
        .filter(Boolean), [edits, groupByKey, pendingFor, pendingBlocked]);

    const blockedCount = pendingList.filter(p => p.blocked).length;

    // ── Editing actions ──────────────────────────────────────────────────────

    const updateEdit = (group, patch) => {
        setEdits(prev => {
            const current = prev[group.key] || { group, clear: {} };
            return { ...prev, [group.key]: { ...current, ...patch(current), group } };
        });
        setResults(prev => ({ ...prev, [group.key]: undefined }));
    };

    const onChangeValue = group => (locale, value) =>
        updateEdit(group, () => ({ [locale]: value }));
    const onClear = group => locale =>
        updateEdit(group, current => ({ [locale]: undefined, clear: { ...(current.clear || {}), [locale]: true } }));
    const onUndoClear = group => locale =>
        updateEdit(group, current => ({ clear: { ...(current.clear || {}), [locale]: false } }));

    const discardLocalEdits = key => setEdits(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
    });

    const saveDrafts = entries => {
        const ready = entries.filter(e => !e.blocked);
        if (!ready.length) return;
        const items = ready.map(({ group, pending }) => {
            const item = { ref: group.ref, key: group.key };
            if (pending.en !== undefined) item.en = pending.en;
            if (pending.ar !== undefined) item.ar = pending.ar;
            if (pending.clear.length) item.clear = pending.clear;
            return item;
        });
        setSaving(true);
        setNotice(null);
        httpClient(`${API}/drafts`, { method: 'PATCH', body: JSON.stringify({ items }) })
            .then(({ json }) => {
                const errors = (json && json.errors) || [];
                const nextResults = {};
                const keepEdits = {};
                ready.forEach(({ key, pending }, index) => {
                    const locales = LOCALES.filter(l => pending[l] !== undefined || pending.clear.includes(l));
                    const forItem = errors.filter(e => e.index === index || (e.index === undefined && e.key === key));
                    nextResults[key] = {};
                    locales.forEach(locale => {
                        const issues = forItem.filter(e => !e.locale || e.locale === locale);
                        const failed = issues.filter(e => !e.saved);
                        const flagged = issues.filter(e => e.saved);
                        if (failed.length) {
                            nextResults[key][locale] = { state: 'error', message: `Not saved: ${failed.map(issueText).join(', ')}` };
                            keepEdits[key] = true;
                        } else if (flagged.length) {
                            nextResults[key][locale] = { state: 'flagged', message: `Saved as draft, flagged: ${flagged.map(issueText).join(', ')}` };
                        } else {
                            nextResults[key][locale] = { state: 'saved', message: pending.clear.includes(locale) ? 'Saved — reverts to bundled on publish' : 'Draft saved' };
                        }
                    });
                });
                setResults(prev => ({ ...prev, ...nextResults }));
                setEdits(prev => {
                    const next = { ...prev };
                    ready.forEach(({ key }) => { if (!keepEdits[key]) delete next[key]; });
                    return next;
                });
                const failedCount = Object.keys(keepEdits).length;
                setNotice({
                    tone: failedCount ? 'warn' : 'success',
                    text: `${json && typeof json.saved === 'number' ? json.saved : 0} string(s) saved as drafts${failedCount ? `; ${failedCount} key(s) need fixes` : ''}.`,
                });
                refreshAll();
            })
            .catch(err => {
                console.error('Translations save drafts failed:', err);
                setNotice({ tone: 'error', text: `Could not save drafts (${readError(err).code}).` });
            })
            .finally(() => setSaving(false));
    };

    const discardServerDrafts = group => {
        setSaving(true);
        httpClient(`${API}/drafts/discard`, { method: 'POST', body: JSON.stringify({ refs: [group.ref] }) })
            .then(({ json }) => {
                discardLocalEdits(group.key);
                setResults(prev => ({ ...prev, [group.key]: undefined }));
                setNotice({ tone: 'success', text: `${(json && json.discarded) || 0} draft(s) discarded for ${group.ref}.` });
                refreshAll();
            })
            .catch(err => {
                console.error('Translations discard drafts failed:', err);
                setNotice({ tone: 'error', text: `Could not discard drafts (${readError(err).code}).` });
            })
            .finally(() => setSaving(false));
    };

    const copyRef = ref => {
        copyText(ref)
            .then(() => {
                setCopiedRef(ref);
                setTimeout(() => setCopiedRef(current => (current === ref ? null : current)), 1500);
            })
            .catch(err => console.error('Translations copy ref failed:', err));
    };

    // ── CSV export ───────────────────────────────────────────────────────────

    const exportCsv = () => {
        const qs = new URLSearchParams({ scope: exportScope });
        if (platform !== 'all') qs.set('platform', platform);
        setExporting(true);
        httpClient(`${API}/export?${qs.toString()}`, { headers: new Headers({ Accept: 'text/csv' }) })
            .then(res => {
                const text = (res && res.body) || '';
                // fetch().text() strips the UTF-8 BOM; put it back so Excel reads Arabic correctly.
                const content = text.charCodeAt(0) === 0xfeff ? text : `﻿${text}`;
                const fallback = `skilz-copy-v${(summary && summary.version) || 0}-${exportScope}.csv`;
                const disposition = res && res.headers && res.headers.get ? res.headers.get('Content-Disposition') : '';
                const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filenameFrom(disposition, fallback);
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            })
            .catch(err => {
                console.error('Translations export failed:', err);
                setNotice({ tone: 'error', text: `Export failed (${readError(err).code}).` });
            })
            .finally(() => setExporting(false));
    };

    // ── CSV import ───────────────────────────────────────────────────────────

    const resetImport = () => {
        setImportState({ fileName: '', content: '', preview: null, result: null, loading: false, error: null });
        if (fileInput.current) fileInput.current.value = '';
    };

    const runImport = (content, dryRun) => {
        setImportState(s => ({ ...s, loading: true, error: null }));
        httpClient(`${API}/import${dryRun ? '?dryRun=1' : ''}`, {
            method: 'POST',
            body: JSON.stringify({ format: 'csv', content }),
        })
            .then(({ json }) => {
                if (dryRun) {
                    setImportState(s => ({ ...s, loading: false, preview: json || {} }));
                } else {
                    setImportState(s => ({ ...s, loading: false, result: json || {} }));
                    refreshAll();
                }
            })
            .catch(err => {
                console.error(`Translations import ${dryRun ? 'dry run' : 'apply'} failed:`, err);
                setImportState(s => ({ ...s, loading: false, error: readError(err).code }));
            });
    };

    const onPickFile = e => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        if (file.size > MAX_IMPORT_BYTES) {
            setImportState(s => ({ ...s, fileName: file.name, error: 'File is larger than 5 MB.', preview: null, result: null }));
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const content = String(reader.result || '');
            setImportState({ fileName: file.name, content, preview: null, result: null, loading: false, error: null });
            runImport(content, true);
        };
        reader.onerror = () => {
            console.error('Translations import read failed:', reader.error);
            setImportState(s => ({ ...s, error: 'Could not read the file.' }));
        };
        reader.readAsText(file, 'utf-8');
    };

    // ── Publish ──────────────────────────────────────────────────────────────

    const draftsPending = (summary && summary.draftsPending) || 0;
    const overLimitDrafts = (summary && summary.overLimitDrafts) || 0;
    const publishDisabled = !draftsPending || overLimitDrafts > 0;

    const publish = () => {
        setPublishBusy(true);
        setPublishOutcome(null);
        httpClient(`${API}/publish`, { method: 'POST', body: JSON.stringify({ notes: publishNotes.trim() }) })
            .then(({ json }) => {
                setPublishOutcome({ ok: true, version: json && json.version, changeCount: (json && json.changeCount) || 0, skipped: (json && json.skipped) || [] });
                setPublishNotes('');
                refreshAll();
            })
            .catch(err => {
                console.error('Translations publish failed:', err);
                const { code, details } = readError(err);
                if (err && err.status === 422) {
                    setPublishOutcome({ ok: false, code, invalid: Array.isArray(details.errors) ? details.errors : [] });
                } else if (err && err.status === 409) {
                    setPublishOutcome({ ok: false, code, skipped: details.skipped || [] });
                } else {
                    setPublishOutcome({ ok: false, code });
                }
                refreshAll();
            })
            .finally(() => setPublishBusy(false));
    };

    // ── Releases / rollback ──────────────────────────────────────────────────

    const toggleRelease = version => {
        if (releaseDetail[version]) {
            setReleaseDetail(prev => ({ ...prev, [version]: undefined }));
            return;
        }
        setReleaseDetail(prev => ({ ...prev, [version]: { loading: true } }));
        httpClient(`${API}/releases/${version}`)
            .then(({ json }) => setReleaseDetail(prev => ({ ...prev, [version]: { loading: false, data: json || {} } })))
            .catch(err => {
                console.error('Translations release detail failed:', err);
                setReleaseDetail(prev => ({ ...prev, [version]: { loading: false, error: readError(err).code } }));
            });
    };

    const rollback = () => {
        if (!rollbackTarget) return;
        setRollbackBusy(true);
        setRollbackOutcome(null);
        httpClient(`${API}/releases/${rollbackTarget}/rollback`, { method: 'POST', body: JSON.stringify({ notes: rollbackNotes.trim() }) })
            .then(({ json }) => {
                setRollbackOutcome({
                    ok: true,
                    version: json && json.version,
                    rollbackOf: json && json.rollbackOf,
                    changeCount: (json && json.changeCount) || 0,
                    conflicts: (json && json.conflicts) || [],
                    invalid: (json && json.invalid) || [],
                });
                setRollbackNotes('');
                refreshAll();
            })
            .catch(err => {
                console.error('Translations rollback failed:', err);
                const { code, details } = readError(err);
                setRollbackOutcome({ ok: false, code, conflicts: details.conflicts || [], invalid: details.invalid || [] });
            })
            .finally(() => setRollbackBusy(false));
    };

    // ── Kill switch ──────────────────────────────────────────────────────────

    const overlayEnabled = !summary || summary.enabled !== false;

    const toggleKillSwitch = () => {
        setKillBusy(true);
        setKillError(null);
        httpClient(`${API}/kill-switch`, { method: 'POST', body: JSON.stringify({ enabled: !overlayEnabled, notes: killNotes.trim() }) })
            .then(({ json }) => {
                setKillOpen(false);
                setKillNotes('');
                setNotice({
                    tone: 'success',
                    text: `Overlay ${json && json.enabled ? 're-enabled' : 'disabled'} in v${json && json.version}. Clients apply it on their next launch.`,
                });
                refreshAll();
            })
            .catch(err => {
                console.error('Translations kill switch failed:', err);
                setKillError(readError(err).code);
            })
            .finally(() => setKillBusy(false));
    };

    // ── Render ───────────────────────────────────────────────────────────────

    if (!permissionsLoaded) {
        return <PageShell><StatePanel state="loading">Checking permissions…</StatePanel></PageShell>;
    }
    if (!canAccess) {
        return (
            <PageShell>
                <StatePanel state="error">You do not have access to Translations. Ask a Super Admin for the Content or Operations role.</StatePanel>
            </PageShell>
        );
    }

    const headerActions = (
        <>
            <select aria-label="Export scope" style={styles.select} value={exportScope} onChange={e => setExportScope(e.target.value)}>
                {EXPORT_SCOPES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <ActionButton type="button" variant="secondary" style={styles.secondaryButton} disabled={exporting} onClick={exportCsv}>
                {exporting ? 'Exporting…' : 'Export CSV'}
            </ActionButton>
            <ActionButton type="button" variant="secondary" style={styles.secondaryButton} onClick={() => { resetImport(); setImportOpen(true); }}>
                Import CSV
            </ActionButton>
            {isSuperAdmin && (
                <ActionButton
                    type="button"
                    disabled={publishDisabled}
                    title={overLimitDrafts > 0 ? 'Fix over-limit drafts before publishing' : undefined}
                    onClick={() => { setPublishOutcome(null); setPublishOpen(true); }}
                >
                    Publish ({draftsPending})
                </ActionButton>
            )}
        </>
    );

    return (
        <PageShell>
            <PageHeader
                eyebrow="System"
                title="App & Web Copy"
                description="Edit EN/AR text. Changes go live on the user's next app launch."
                actions={headerActions}
            />

            {!overlayEnabled && (
                <div style={styles.alertCard} role="alert">
                    The remote copy overlay is switched OFF. Every client shows bundled text on its next launch.
                </div>
            )}
            {summary && summary.unreleasedPublished > 0 && (
                <div style={styles.alertCard} role="alert">
                    {summary.unreleasedPublished} published string(s) are ahead of the latest release. Publish again to bump the version.
                </div>
            )}
            {notice && (
                <div style={styles[notice.tone] || styles.success} role="status">{notice.text}</div>
            )}

            <div className="sk-metric-grid">
                <MetricCard label="Live version" value={summary ? `v${summary.version || 0}` : '—'} detail={summary ? (overlayEnabled ? 'Overlay on' : 'Overlay off') : (summaryError ? 'Summary unavailable' : 'Loading…')} />
                <MetricCard tone="blue" label="Overrides live" value={summary ? summary.overridesLive : '—'} detail={summary ? `${summary.totalKeys} keys total` : undefined} />
                <MetricCard tone="purple" label="Drafts pending" value={summary ? draftsPending : '—'} detail={summary && summary.staleOverrides ? `${summary.staleOverrides} stale overrides` : undefined} />
                <MetricCard tone={overLimitDrafts > 0 ? 'orange' : 'blue'} label="Over limit" value={summary ? overLimitDrafts : '—'} detail={overLimitDrafts > 0 ? 'Blocks publish' : 'Nothing blocking'} />
            </div>

            <Surface
                title="Strings"
                description="One row per key, sorted by ref. Each locale has its own length limit."
                actions={(
                    <ActionButton
                        type="button"
                        disabled={saving || !pendingList.length || pendingList.length === blockedCount}
                        onClick={() => saveDrafts(pendingList)}
                    >
                        {saving ? 'Saving…' : `Save drafts (${pendingList.length - blockedCount})`}
                    </ActionButton>
                )}
            >
                <nav className="sk-ds__tabs" role="tablist" aria-label="Translation status">
                    {TABS.map(t => (
                        <button
                            key={t.id}
                            role="tab"
                            type="button"
                            aria-selected={tab === t.id}
                            className={`sk-ds__tab ${tab === t.id ? 'active' : ''} ${t.tone}`}
                            onClick={() => setTab(t.id)}
                        >
                            <span className="sk-ds__tab-label">{t.label}</span>
                            <span className="sk-ds__tab-count">{summary && typeof summary[t.summaryKey] === 'number' ? summary[t.summaryKey] : '—'}</span>
                        </button>
                    ))}
                </nav>

                <div style={{ ...styles.stack, margin: '16px 0 4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <input
                            type="search"
                            aria-label="Search strings"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            placeholder="Search by ref (SK-0042 or 42), key, EN or AR text…"
                            style={styles.search}
                        />
                        <select aria-label="Platform" style={styles.select} value={platform} onChange={e => setPlatform(e.target.value)}>
                            <option value="all">All platforms</option>
                            <option value="app">App</option>
                            <option value="web">Web</option>
                        </select>
                        <select aria-label="Tier" style={styles.select} value={tier} onChange={e => setTier(e.target.value)}>
                            <option value="all">All tiers</option>
                            <option value="tight">Tight</option>
                            <option value="normal">Normal</option>
                            <option value="long">Long</option>
                        </select>
                        <span style={{ marginLeft: 'auto', ...styles.muted }}>{visible.length} key{visible.length !== 1 ? 's' : ''}</span>
                    </div>
                    {Object.keys(areaCounts).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            <button type="button" style={styles.pillButton(area === 'all')} onClick={() => setArea('all')}>
                                All ({groups.length})
                            </button>
                            {Object.keys(areaCounts).sort().map(a => (
                                <button key={a} type="button" style={styles.pillButton(area === a)} onClick={() => setArea(area === a ? 'all' : a)}>
                                    {a} ({areaCounts[a]})
                                </button>
                            ))}
                        </div>
                    )}
                    {blockedCount > 0 && (
                        <div style={styles.error}>{blockedCount} edited key(s) have errors (placeholders, empty or invalid characters) and will not be saved until fixed.</div>
                    )}
                </div>

                {loading && <StatePanel state="loading">Loading strings…</StatePanel>}
                {!loading && loadError && (
                    <StatePanel state="error" actions={<ActionButton type="button" onClick={() => setReloadToken(t => t + 1)}>Retry</ActionButton>}>
                        Strings could not be loaded ({loadError}).
                    </StatePanel>
                )}
                {!loading && !loadError && visible.length === 0 && (
                    <Empty>{search ? `No strings match “${search}”.` : 'No strings in this view.'}</Empty>
                )}

                {!loading && !loadError && pageRows.map(group => {
                    const isOpen = !!expanded[group.key];
                    const edit = edits[group.key];
                    const pending = pendingFor(group, edit);
                    const blocked = pending ? pendingBlocked(group, pending) : false;
                    const views = [group.en, group.ar].filter(Boolean);
                    const hasDraft = views.some(v => v.status === 'draft');
                    const hasOverride = views.some(v => v.publishedValue !== null && v.publishedValue !== undefined);
                    const overLimit = views.some(v => v.overLimit);
                    const stale = views.some(v => v.stale);
                    return (
                        <Row
                            key={group.key}
                            className={isOpen ? 'is-editing' : ''}
                            right={(
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    {pending && <Tag color={tokens.violet}>unsaved</Tag>}
                                    <ActionButton
                                        type="button"
                                        variant="secondary"
                                        style={styles.secondaryButton}
                                        aria-expanded={isOpen}
                                        onClick={() => setExpanded(prev => ({ ...prev, [group.key]: !isOpen }))}
                                    >
                                        {isOpen ? 'Close' : 'Edit'}
                                    </ActionButton>
                                </div>
                            )}
                        >
                            <RowText>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <strong style={{ ...styles.mono, fontSize: 15, color: tokens.ink }}>{group.ref || '—'}</strong>
                                    {group.ref && (
                                        <button type="button" style={styles.linkButton} onClick={() => copyRef(group.ref)} aria-label={`Copy ${group.ref}`}>
                                            {copiedRef === group.ref ? 'Copied' : 'Copy'}
                                        </button>
                                    )}
                                    <span style={{ ...styles.mono, fontSize: 12, color: '#7f8999', overflowWrap: 'anywhere' }}>{group.key}</span>
                                </div>
                                <RowMeta>
                                    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <span>{group.area || 'general'}</span>
                                        <span>·</span>
                                        <span>{group.tier || 'normal'}{group.tier === 'tight' ? ' (button/tab/badge)' : ''}</span>
                                        {group.platforms && group.platforms.app && <Tag color={tokens.blue}>app</Tag>}
                                        {group.platforms && group.platforms.web && <Tag color={tokens.violet}>web</Tag>}
                                        {hasDraft && <StatusPill status="draft" />}
                                        {hasOverride && <StatusPill status="published" />}
                                        {overLimit && <Tag color={tokens.danger}>over limit</Tag>}
                                        {stale && <Tag color={tokens.info}>stale</Tag>}
                                    </span>
                                </RowMeta>
                                {!isOpen && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 6, fontSize: 13 }}>
                                        <span style={{ overflowWrap: 'anywhere' }}><strong style={styles.muted}>EN </strong>{group.en ? serverValue(group.en) || '—' : '—'}</span>
                                        <span dir="rtl" style={{ overflowWrap: 'anywhere', textAlign: 'right' }}>{group.ar ? serverValue(group.ar) || '—' : '—'}<strong style={styles.muted}> AR</strong></span>
                                    </div>
                                )}
                            </RowText>

                            {isOpen && (
                                <div className="sk-row-editor" style={{ ...styles.stack, marginTop: 18, padding: '18px 4px 4px' }}>
                                    {LOCALES.map(locale => (
                                        <LocaleEditor
                                            key={locale}
                                            group={group}
                                            locale={locale}
                                            edit={edit}
                                            result={results[group.key] && results[group.key][locale]}
                                            onChange={onChangeValue(group)}
                                            onClear={onClear(group)}
                                            onUndo={onUndoClear(group)}
                                        />
                                    ))}
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <ActionButton
                                            type="button"
                                            disabled={saving || !pending || blocked}
                                            onClick={() => saveDrafts([{ key: group.key, group, pending, blocked }])}
                                        >
                                            {saving ? 'Saving…' : 'Save draft'}
                                        </ActionButton>
                                        {edit && (
                                            <ActionButton type="button" variant="secondary" style={styles.secondaryButton} disabled={saving} onClick={() => discardLocalEdits(group.key)}>
                                                Reset edits
                                            </ActionButton>
                                        )}
                                        {hasDraft && (
                                            <ActionButton type="button" variant="danger" disabled={saving} onClick={() => discardServerDrafts(group)}>
                                                Discard saved draft
                                            </ActionButton>
                                        )}
                                    </div>
                                    {isSuperAdmin && <MetaEditor key={`${group.ref}-${group.tier}-${group.en && group.en.maxLength}-${group.ar && group.ar.maxLength}`} group={group} onSaved={refreshAll} />}
                                </div>
                            )}
                        </Row>
                    );
                })}

                {!loading && !loadError && pageCount > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '12px 0 4px' }}>
                        <ActionButton type="button" variant="secondary" style={styles.secondaryButton} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</ActionButton>
                        <span style={styles.muted}>Page {page} of {pageCount}</span>
                        <ActionButton type="button" variant="secondary" style={styles.secondaryButton} disabled={page >= pageCount} onClick={() => setPage(p => p + 1)}>Next</ActionButton>
                    </div>
                )}
            </Surface>

            <Surface title="Change log" description="Every publish, rollback and kill-switch change is a new version.">
                {releasesLoading && <StatePanel state="loading">Loading releases…</StatePanel>}
                {!releasesLoading && releasesError && (
                    <StatePanel state="error" actions={<ActionButton type="button" onClick={loadReleases}>Retry</ActionButton>}>
                        Releases could not be loaded.
                    </StatePanel>
                )}
                {!releasesLoading && !releasesError && releases.length === 0 && <Empty>Nothing has been published yet.</Empty>}
                {!releasesLoading && !releasesError && releases.map(r => {
                    const detail = releaseDetail[r.version];
                    const canRollback = isSuperAdmin && r.kind !== 'kill_switch' && (r.changeCount || 0) > 0;
                    return (
                        <Row
                            key={r.version}
                            right={(
                                <div style={{ display: 'flex', gap: 8 }}>
                                    {(r.changeCount || 0) > 0 && (
                                        <ActionButton type="button" variant="secondary" style={styles.secondaryButton} onClick={() => toggleRelease(r.version)}>
                                            {detail ? 'Hide changes' : 'View changes'}
                                        </ActionButton>
                                    )}
                                    {canRollback && (
                                        <ActionButton type="button" variant="danger" onClick={() => { setRollbackOutcome(null); setRollbackNotes(''); setRollbackTarget(r.version); }}>
                                            Rollback
                                        </ActionButton>
                                    )}
                                </div>
                            )}
                        >
                            <RowText>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <strong style={styles.mono}>v{r.version}</strong>
                                    <Tag color={r.kind === 'publish' ? tokens.success : r.kind === 'rollback' ? tokens.warning : tokens.danger}>
                                        {r.kind === 'kill_switch' ? `kill switch · ${r.enabled === false ? 'off' : 'on'}` : r.kind}
                                    </Tag>
                                    {r.rollbackOf && <span style={styles.muted}>of v{r.rollbackOf}</span>}
                                    <span style={styles.muted}>{r.changeCount || 0} change(s)</span>
                                </div>
                                <RowMeta>
                                    {(r.publishedBy && r.publishedBy.name) || 'Unknown admin'} · {formatDate(r.createdAt)}{r.notes ? ` · ${r.notes}` : ''}
                                </RowMeta>
                            </RowText>
                            {detail && detail.loading && <StatePanel state="loading">Loading changes…</StatePanel>}
                            {detail && detail.error && <div style={styles.error}>Could not load changes ({detail.error}).</div>}
                            {detail && detail.data && (
                                <div className="sk-table-wrap" style={{ marginTop: 10 }}>
                                    <table className="sk-data-table">
                                        <thead>
                                            <tr><th>Ref</th><th>Key</th><th>Locale</th><th>From</th><th>To</th></tr>
                                        </thead>
                                        <tbody>
                                            {(detail.data.changes || []).map((c, i) => (
                                                <tr key={i}>
                                                    <td style={styles.mono}>{c.ref || '—'}</td>
                                                    <td style={styles.mono}>{c.key || '—'}</td>
                                                    <td>{(c.locale || '').toUpperCase()}</td>
                                                    <td dir={c.locale === 'ar' ? 'rtl' : 'ltr'}>{c.from === null || c.from === undefined ? <em>bundled</em> : c.from}</td>
                                                    <td dir={c.locale === 'ar' ? 'rtl' : 'ltr'}>{c.to === null || c.to === undefined ? <em>bundled</em> : c.to}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </Row>
                    );
                })}
            </Surface>

            {isSuperAdmin && (
                <Surface title="Overlay kill switch" description="Turns remote copy off (or back on) for every app and web client on their next launch.">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <Tag color={overlayEnabled ? tokens.success : tokens.danger}>{overlayEnabled ? 'Overlay on' : 'Overlay off'}</Tag>
                        <ActionButton
                            type="button"
                            variant={overlayEnabled ? 'danger' : 'primary'}
                            disabled={!summary}
                            onClick={() => { setKillError(null); setKillNotes(''); setKillOpen(true); }}
                        >
                            {overlayEnabled ? 'Turn overlay off' : 'Turn overlay back on'}
                        </ActionButton>
                    </div>
                </Surface>
            )}

            {importOpen && (
                <Modal
                    title="Import CSV"
                    description="You can upload only the rows you want to change. Rows are matched by ref."
                    busy={importState.loading}
                    onClose={() => setImportOpen(false)}
                >
                    <div style={styles.stack}>
                        <p style={{ margin: 0, fontSize: 13, color: '#4a5365' }}>
                            Export a CSV, edit the <code>en</code> and/or <code>ar</code> cells, delete any rows you are not changing, and upload it here.
                            Keep the <code>ref</code> column (or <code>key</code>). Empty cells and unchanged values are ignored, and rows not in the sheet are left alone.
                            Imported values become drafts — nothing goes live until a Super Admin publishes.
                        </p>
                        <label style={styles.label}>
                            CSV file (UTF-8)
                            <input
                                ref={fileInput}
                                type="file"
                                accept=".csv,text/csv"
                                onChange={onPickFile}
                                disabled={importState.loading}
                                style={{ display: 'block', marginTop: 6 }}
                            />
                        </label>
                        {importState.loading && <StatePanel state="loading">{importState.preview ? 'Importing…' : 'Checking file…'}</StatePanel>}
                        {importState.error && <div style={styles.error}>Import failed: {importState.error}</div>}

                        {importState.preview && !importState.result && (
                            <ImportPreview preview={importState.preview} />
                        )}

                        {importState.result && (
                            <div style={styles.success}>
                                Imported {importState.result.changed || 0} change(s) as drafts{importState.result.skipped ? `; ${importState.result.skipped} row(s) skipped` : ''}.
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {importState.preview && !importState.result && (
                                <ActionButton
                                    type="button"
                                    disabled={importState.loading || !(importState.preview.changed > 0)}
                                    onClick={() => runImport(importState.content, false)}
                                >
                                    Apply {importState.preview.changed || 0} change(s) as drafts
                                </ActionButton>
                            )}
                            <ActionButton type="button" variant="secondary" style={styles.secondaryButton} disabled={importState.loading} onClick={() => setImportOpen(false)}>
                                {importState.result ? 'Done' : 'Cancel'}
                            </ActionButton>
                        </div>
                    </div>
                </Modal>
            )}

            {publishOpen && isSuperAdmin && (
                <Modal title="Publish drafts" busy={publishBusy} onClose={() => setPublishOpen(false)}>
                    <div style={styles.stack}>
                        {!publishOutcome || !publishOutcome.ok ? (
                            <>
                                <p style={{ margin: 0, fontSize: 13, color: '#4a5365' }}>
                                    {draftsPending} draft string(s) will go live on every client&apos;s next launch.
                                    Any draft that fails validation blocks the whole publish.
                                </p>
                                {pendingList.length > 0 && (
                                    <div style={styles.warn}>{pendingList.length} key(s) have unsaved edits on this page. Unsaved edits are not published.</div>
                                )}
                                <label style={styles.label}>
                                    Release notes (required)
                                    <textarea style={{ ...styles.textarea, marginTop: 4 }} maxLength={500} value={publishNotes} onChange={e => setPublishNotes(e.target.value)} />
                                </label>
                                {publishOutcome && !publishOutcome.ok && <PublishError outcome={publishOutcome} />}
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <ActionButton type="button" disabled={publishBusy || !publishNotes.trim()} onClick={publish}>
                                        {publishBusy ? 'Publishing…' : 'Confirm publish'}
                                    </ActionButton>
                                    <ActionButton type="button" variant="secondary" style={styles.secondaryButton} disabled={publishBusy} onClick={() => setPublishOpen(false)}>
                                        Cancel
                                    </ActionButton>
                                </div>
                            </>
                        ) : (
                            <>
                                <div style={styles.success}>Published v{publishOutcome.version} with {publishOutcome.changeCount} change(s).</div>
                                {publishOutcome.skipped.length > 0 && (
                                    <div style={styles.warn}>
                                        {publishOutcome.skipped.length} string(s) were NOT published because they were edited during the publish. They are still drafts:
                                        <IssueList items={publishOutcome.skipped} render={s => `${describeItem(s)} — ${issueText(s)}`} />
                                    </div>
                                )}
                                <div>
                                    <ActionButton type="button" onClick={() => setPublishOpen(false)}>Done</ActionButton>
                                </div>
                            </>
                        )}
                    </div>
                </Modal>
            )}

            {rollbackTarget !== null && isSuperAdmin && (
                <Modal title={`Roll back v${rollbackTarget}`} busy={rollbackBusy} onClose={() => setRollbackTarget(null)}>
                    <div style={styles.stack}>
                        {!rollbackOutcome || !rollbackOutcome.ok ? (
                            <>
                                <p style={{ margin: 0, fontSize: 13, color: '#4a5365' }}>
                                    Restores the values from before v{rollbackTarget} as a new release. Strings changed since then are skipped and listed.
                                </p>
                                <label style={styles.label}>
                                    Notes
                                    <textarea style={{ ...styles.textarea, marginTop: 4 }} maxLength={500} value={rollbackNotes} onChange={e => setRollbackNotes(e.target.value)} />
                                </label>
                                {rollbackOutcome && !rollbackOutcome.ok && (
                                    <div style={styles.error}>
                                        {rollbackOutcome.code === 'NOTHING_TO_ROLLBACK' ? 'Nothing could be rolled back.' : `Rollback failed (${rollbackOutcome.code}).`}
                                        <IssueList items={rollbackOutcome.conflicts} render={c => `${describeItem(c)} — ${issueText(c)}`} />
                                        <IssueList items={rollbackOutcome.invalid} render={c => `${describeItem(c)} — ${issueText(c)}: ${(c.errors || []).map(issueText).join(', ')}`} />
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <ActionButton type="button" variant="danger" disabled={rollbackBusy} onClick={rollback}>
                                        {rollbackBusy ? 'Rolling back…' : 'Confirm rollback'}
                                    </ActionButton>
                                    <ActionButton type="button" variant="secondary" style={styles.secondaryButton} disabled={rollbackBusy} onClick={() => setRollbackTarget(null)}>
                                        Cancel
                                    </ActionButton>
                                </div>
                            </>
                        ) : (
                            <>
                                <div style={styles.success}>
                                    Rolled back v{rollbackOutcome.rollbackOf} as v{rollbackOutcome.version} ({rollbackOutcome.changeCount} change(s)).
                                </div>
                                {rollbackOutcome.invalid.length > 0 && (
                                    <div style={styles.warn}>
                                        {rollbackOutcome.invalid.length} string(s) were NOT restored because the old value no longer passes validation:
                                        <IssueList items={rollbackOutcome.invalid} render={c => `${describeItem(c)} — ${(c.errors || []).map(issueText).join(', ') || issueText(c)}`} />
                                    </div>
                                )}
                                {rollbackOutcome.conflicts.length > 0 && (
                                    <div style={styles.warn}>
                                        {rollbackOutcome.conflicts.length} string(s) were skipped because they changed after v{rollbackOutcome.rollbackOf}:
                                        <IssueList items={rollbackOutcome.conflicts} render={c => `${describeItem(c)} — ${issueText(c)}`} />
                                    </div>
                                )}
                                <div>
                                    <ActionButton type="button" onClick={() => setRollbackTarget(null)}>Done</ActionButton>
                                </div>
                            </>
                        )}
                    </div>
                </Modal>
            )}

            {killOpen && isSuperAdmin && (
                <Modal title={overlayEnabled ? 'Turn the overlay off' : 'Turn the overlay back on'} busy={killBusy} onClose={() => setKillOpen(false)}>
                    <div style={styles.stack}>
                        <p style={{ margin: 0, fontSize: 13, color: '#4a5365' }}>
                            {overlayEnabled
                                ? 'Every app and web client drops remote copy and shows bundled text on its next launch. Drafts and overrides are kept.'
                                : 'Clients start showing the published overrides again on their next launch.'}
                        </p>
                        <label style={styles.label}>
                            Reason (required)
                            <textarea style={{ ...styles.textarea, marginTop: 4 }} maxLength={500} value={killNotes} onChange={e => setKillNotes(e.target.value)} />
                        </label>
                        {killError && <div style={styles.error}>Could not change the kill switch ({killError}).</div>}
                        <div style={{ display: 'flex', gap: 8 }}>
                            <ActionButton type="button" variant={overlayEnabled ? 'danger' : 'primary'} disabled={killBusy || !killNotes.trim()} onClick={toggleKillSwitch}>
                                {killBusy ? 'Saving…' : overlayEnabled ? 'Turn off' : 'Turn on'}
                            </ActionButton>
                            <ActionButton type="button" variant="secondary" style={styles.secondaryButton} disabled={killBusy} onClick={() => setKillOpen(false)}>
                                Cancel
                            </ActionButton>
                        </div>
                    </div>
                </Modal>
            )}
        </PageShell>
    );
};

// ── Import preview / publish error ───────────────────────────────────────────

const ImportPreview = ({ preview }) => {
    const errors = preview.errors || [];
    const flagged = errors.filter(e => e.saved !== undefined && e.code === 'OVER_LIMIT');
    const blocking = errors.filter(e => !flagged.includes(e));
    const rows = (preview.preview || []).slice(0, PREVIEW_LIMIT);
    return (
        <div style={styles.stack}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Tag color={tokens.info}>matched by {preview.matchedBy || '—'}</Tag>
                <Tag color={tokens.success}>{preview.changed || 0} changed</Tag>
                <Tag color={tokens.muted}>{preview.unchanged || 0} unchanged</Tag>
                {preview.empty ? <Tag color={tokens.muted}>{preview.empty} empty</Tag> : null}
                {preview.unknownRefs ? <Tag color={tokens.danger}>{preview.unknownRefs} unknown refs</Tag> : null}
                {preview.unknownKeys ? <Tag color={tokens.danger}>{preview.unknownKeys} unknown keys</Tag> : null}
                {blocking.length ? <Tag color={tokens.warning}>{blocking.length} will be skipped</Tag> : null}
            </div>
            {preview.matchedBy === 'key' && (
                <div style={styles.warn}>This sheet has no ref column, so rows were matched by key. Prefer exporting with refs.</div>
            )}
            {blocking.length > 0 && (
                <div style={styles.error}>
                    {blocking.length} row error(s) — these rows will be skipped:
                    <IssueList
                        items={blocking.slice(0, PREVIEW_LIMIT)}
                        render={e => `Row ${e.row || '—'} · ${describeItem(e)} — ${issueText(e)}`}
                    />
                </div>
            )}
            {flagged.length > 0 && (
                <div style={styles.warn}>
                    {flagged.length} value(s) are over their limit. They will be saved as drafts but cannot be published until shortened:
                    <IssueList items={flagged.slice(0, PREVIEW_LIMIT)} render={e => `Row ${e.row || '—'} · ${describeItem(e)}`} />
                </div>
            )}
            {rows.length > 0 ? (
                <div className="sk-table-wrap">
                    <table className="sk-data-table">
                        <thead>
                            <tr><th>Row</th><th>Ref</th><th>Key</th><th>Locale</th><th>From</th><th>To</th><th>Length</th></tr>
                        </thead>
                        <tbody>
                            {rows.map((p, i) => (
                                <tr key={i}>
                                    <td>{p.row || '—'}</td>
                                    <td style={styles.mono}>{p.ref || '—'}</td>
                                    <td style={styles.mono}>{p.key || '—'}</td>
                                    <td>{(p.locale || '').toUpperCase()}</td>
                                    <td dir={p.locale === 'ar' ? 'rtl' : 'ltr'}>{p.from === null || p.from === undefined ? '—' : p.from}</td>
                                    <td dir={p.locale === 'ar' ? 'rtl' : 'ltr'}>{p.to || '—'}</td>
                                    <td style={{ color: p.length > p.maxLength ? COUNTER_RED : undefined, whiteSpace: 'nowrap' }}>{p.length} / {p.maxLength || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {(preview.preview || []).length > PREVIEW_LIMIT && (
                        <span style={styles.muted}>Showing the first {PREVIEW_LIMIT} of {(preview.preview || []).length} changes.</span>
                    )}
                </div>
            ) : (
                <Empty>No changes found in this file.</Empty>
            )}
        </div>
    );
};

const PublishError = ({ outcome }) => {
    if (outcome.code === 'VALIDATION_FAILED') {
        return (
            <div style={styles.error}>
                Publish blocked — nothing was published. Fix these drafts first:
                <IssueList items={outcome.invalid} render={e => `${describeItem(e)} — ${issueText(e)}`} />
            </div>
        );
    }
    if (outcome.code === 'NOTHING_TO_PUBLISH') {
        return (
            <div style={styles.warn}>
                Nothing to publish.
                {(outcome.skipped || []).length > 0 && ' These drafts changed while publishing and were left as drafts:'}
                <IssueList items={outcome.skipped} render={s => `${describeItem(s)} — ${issueText(s)}`} />
            </div>
        );
    }
    return <div style={styles.error}>Publish failed ({outcome.code}).</div>;
};

export default Translations;
